import { useState, useEffect } from 'react';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../../lib/supabase';
import { Badge, Button, Card, Dropdown, Input, KebabMenu, SidePanel } from '../ui';

interface MonthlyExpense {
  id: string;
  month: number;
  year: number;
  amount: number;
  notes: string | null;
  updated_at: string | null;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const getMonthName = (month: number, style: 'long' | 'short' = 'long') =>
  new Date(2000, month - 1, 1).toLocaleDateString('en-US', { month: style });

export default function MonthlyExpenseManager() {
  const [expenses, setExpenses] = useState<MonthlyExpense[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    amount: '',
    notes: '',
  });

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      const { data, error } = await supabase
        .from('financial_expenses')
        .select('*')
        .order('year', { ascending: false })
        .order('month', { ascending: false });

      if (error) throw error;
      setExpenses(data || []);
    } catch (err) {
      console.error('Error fetching expenses:', err);
    }
  };

  const handleSave = async () => {
    const numAmount = parseFloat(formData.amount);
    if (isNaN(numAmount) || numAmount < 0) {
      alert('Please enter a valid positive number');
      return;
    }

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('financial_expenses')
        .upsert(
          {
            month: formData.month,
            year: formData.year,
            amount: numAmount,
            notes: formData.notes || null,
            updated_by: user.id,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: 'month,year',
          }
        );

      if (error) throw error;

      await fetchExpenses();
      setIsAddingNew(false);
      setEditingId(null);
      setFormData({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        amount: '',
        notes: '',
      });
    } catch (err) {
      console.error('Error saving expense:', err);
      alert(err instanceof Error ? err.message : 'Failed to save expense');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (expense: MonthlyExpense) => {
    setEditingId(expense.id);
    setFormData({
      month: expense.month,
      year: expense.year,
      amount: expense.amount.toString(),
      notes: expense.notes || '',
    });
    setIsAddingNew(false);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAddingNew(false);
    setFormData({
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      amount: '',
      notes: '',
    });
  };

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const currentEntry = expenses.find((e) => e.year === currentYear && e.month === currentMonth);
  const title = currentEntry
    ? `${formatCurrency(currentEntry.amount)} in ${getMonthName(currentMonth)}`
    : `No entry for ${getMonthName(currentMonth)} yet`;

  const panelOpen = isAddingNew || editingId !== null;

  return (
    <>
      <Card
        eyebrow="Monthly Expenses"
        title={title}
        actions={
          <Button size="sm" variant="secondary" icon="plus" onClick={() => setIsAddingNew(true)}>
            Add Month
          </Button>
        }
      >
        {expenses.length === 0 ? (
          <p
            style={{
              margin: 0,
              padding: 'var(--space-8) 0',
              textAlign: 'center',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-faint)',
            }}
          >
            No historical expenses recorded yet
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {expenses.map((expense) => (
              <div
                key={expense.id}
                className="hover-wash"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px',
                  margin: '0 -10px',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                    {getMonthName(expense.month)} {expense.year}
                  </span>
                  {expense.notes ? (
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-faint)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {expense.notes}
                    </span>
                  ) : null}
                </span>
                {expense.year === currentYear && expense.month === currentMonth ? (
                  <Badge tone="info">Current</Badge>
                ) : (
                  <Badge tone="idle">Past</Badge>
                )}
                <span
                  className="t-num"
                  style={{ fontSize: 'var(--text-sm)', minWidth: '76px', textAlign: 'right', color: 'var(--text-body)' }}
                >
                  {formatCurrency(expense.amount)}
                </span>
                <KebabMenu
                  label="Expense actions"
                  actions={[{ label: 'Edit Month', icon: faPen, onClick: () => handleEdit(expense) }]}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      <SidePanel
        open={panelOpen}
        onClose={handleCancel}
        eyebrow="Monthly Expenses"
        title={isAddingNew ? 'Add Month' : 'Edit Month'}
        footer={
          <>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="ghost" onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
          </>
        }
      >
        <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Dropdown
              label="Month"
              options={months.map((m) => ({ value: String(m), label: getMonthName(m) }))}
              value={String(formData.month)}
              onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
            />
            <Dropdown
              label="Year"
              options={years.map((y) => String(y))}
              value={String(formData.year)}
              onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
            />
          </div>

          <Input
            label="Amount"
            icon="circle-dollar-sign"
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
          />

          <div>
            <div className="t-eyebrow" style={{ marginBottom: '7px' }}>
              Notes (Optional)
            </div>
            <textarea
              className="ck-textarea"
              rows={3}
              placeholder="Anything notable about this month's spend..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>

          <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            One total per month. Saving an existing month overwrites it and rolls into the P&L.
          </p>
        </div>
      </SidePanel>
    </>
  );
}
